// Zod schemas for workflow action configs
import { z } from 'zod';

/**
 * Base schema for action config items.
 * Used for all entity allocations with percentage-based splits.
 */
export const ActionConfigItemSchema = z.object({
  /**
   * UUID of the entity being allocated (e.g., cost center ID, department ID)
   */
  entityId: z.string().uuid('entityId must be a valid UUID'),

  /**
   * Percentage allocation for this entity.
   * Must be between 0 and 100.
   * All percentages in the items array must sum to exactly 100.
   */
  percentage: z.number().min(0, 'percentage must be >= 0').max(100, 'percentage must be <= 100'),
});

/**
 * Validation helper: check if percentages sum to 100.
 * Allows 0.01 tolerance for floating point precision.
 */
const validatePercentageSum = (items: { percentage: number }[]): boolean => {
  const total = items.reduce((sum, item) => sum + item.percentage, 0);
  return Math.abs(total - 100) < 0.01;
};

/**
 * Schema for cost center action config.
 *
 * Uses a unified structure for both single and split assignments:
 * - Single assignment: `{ items: [{ entityId: "uuid", percentage: 100 }] }`
 * - Split assignment: `{ items: [{ entityId: "uuid-1", percentage: 60 }, { entityId: "uuid-2", percentage: 40 }] }`
 *
 * Validation ensures:
 * - items array has at least 1 item
 * - Each item has valid entityId (UUID) and percentage (0-100)
 * - All percentages sum to exactly 100
 */
export const SetCostCenterActionConfigSchema = z.object({
  items: z
    .array(ActionConfigItemSchema)
    .min(1, 'items must have at least 1 item')
    .refine(validatePercentageSum, {
      message: 'Percentages must sum to 100%',
    }),
});

/**
 * Schema for department action config.
 *
 * Uses a unified structure for both single and split assignments:
 * - Single assignment: `{ items: [{ entityId: "uuid", percentage: 100 }] }`
 * - Split assignment: `{ items: [{ entityId: "uuid-1", percentage: 50 }, { entityId: "uuid-2", percentage: 50 }] }`
 *
 * Validation ensures:
 * - items array has at least 1 item
 * - Each item has valid entityId (UUID) and percentage (0-100)
 * - All percentages sum to exactly 100
 */
export const SetDepartmentActionConfigSchema = z.object({
  items: z
    .array(ActionConfigItemSchema)
    .min(1, 'items must have at least 1 item')
    .refine(validatePercentageSum, {
      message: 'Percentages must sum to 100%',
    }),
});

/**
 * Schema for managerial account action config.
 *
 * Mirrors the standardized structure used by cost center/department actions.
 */
export const SetManagerialAccountActionConfigSchema = z.object({
  items: z
    .array(ActionConfigItemSchema)
    .min(1, 'items must have at least 1 item')
    .refine(validatePercentageSum, {
      message: 'Percentages must sum to 100%',
    }),
});

/**
 * Schema for GL account action config.
 *
 * Uses a unified structure for both single and split assignments:
 * - Single assignment: `{ items: [{ entityId: "uuid", percentage: 100 }] }`
 * - Split assignment: `{ items: [{ entityId: "uuid-1", percentage: 60 }, { entityId: "uuid-2", percentage: 40 }] }`
 */
export const SetGlAccountActionConfigSchema = z.object({
  items: z
    .array(ActionConfigItemSchema)
    .min(1, 'items must have at least 1 item')
    .refine(validatePercentageSum, {
      message: 'Percentages must sum to 100%',
    }),
});

/** Schema for selecting the bill's preferred corporate origin bank account. */
export const SetOriginBankAccountActionConfigSchema = z
  .object({
    bankAccountId: z.string().uuid('bankAccountId must be a valid UUID'),
  })
  .strict();

/** A bill-updated rule sends a draft or submitted bill on to pending approval. No settings. */
export const SubmitForApprovalActionConfigSchema = z.object({}).strict();

/**
 * Base schema for custom segment action config items.
 * Used for segment value allocations with percentage-based splits.
 *
 * Unlike standard entity actions that use `entityId`, segment actions use `valueId`
 * because segments have multiple values and the segment itself is identified separately.
 */
export const SegmentValueConfigItemSchema = z.object({
  /**
   * UUID of the segment value being allocated
   */
  valueId: z.string().uuid('valueId must be a valid UUID'),

  /**
   * Percentage allocation for this segment value.
   * Must be between 0 and 100.
   * All percentages in the items array must sum to exactly 100.
   */
  percentage: z.number().min(0, 'percentage must be >= 0').max(100, 'percentage must be <= 100'),
});

/**
 * Schema for custom segment action config.
 *
 * This schema has a different structure from standard entity actions because:
 * 1. It requires a `segmentId` to identify which segment is being set
 * 2. Items contain `valueId` instead of `entityId` to identify segment values
 *
 * Structure: `{ segmentId: "uuid", items: [{ valueId: "uuid", percentage: 100 }] }`
 *
 * Validation ensures:
 * - segmentId is a valid UUID
 * - items array has at least 1 item
 * - Each item has valid valueId (UUID) and percentage (0-100)
 * - All percentages sum to exactly 100
 */
export const SetCustomSegmentActionConfigSchema = z.object({
  /**
   * UUID of the segment this action applies to
   */
  segmentId: z.string().uuid('segmentId must be a valid UUID'),

  /**
   * Array of segment value allocations
   */
  items: z
    .array(SegmentValueConfigItemSchema)
    .min(1, 'items must have at least 1 item')
    .refine(validatePercentageSum, {
      message: 'Percentages must sum to 100%',
    }),
});

/**
 * Union schema for all standardized action configs.
 *
 * This schema can validate any standardized action config structure.
 * Currently includes cost center, department, managerial account, GL account, and custom segment configs.
 */
export const StandardizedActionConfigSchema = z.union([
  SetCostCenterActionConfigSchema,
  SetCustomSegmentActionConfigSchema,
  SetDepartmentActionConfigSchema,
  SetGlAccountActionConfigSchema,
  SetManagerialAccountActionConfigSchema,
  SetOriginBankAccountActionConfigSchema,
]);

/**
 * Individual approver configuration schema.
 * Uses a discriminated union to ensure type-safe approver configurations
 * where each type has its own required fields.
 *
 * Note: BILL_REQUESTER_MANAGER is intentionally excluded as it's not yet implemented
 * in the backend ApproverResolverService. It can be added back once implemented.
 */
export const ApproverConfigSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('SPECIFIC_USERS'),
    userIds: z.array(z.string().uuid()).min(1, 'At least one user required'),
  }),
  z.object({
    type: z.literal('ROLE'),
    roleId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('COST_CENTER_OWNER'),
  }),
  z.object({
    type: z.literal('DEPARTMENT_HEAD'),
  }),
  z.object({
    type: z.literal('USER_APPROVAL_AUTHORITY'),
  }),
]);

/**
 * Schema for approval request action config.
 *
 * Supports multiple approval patterns:
 * - SINGLE: exactly one approver must approve
 * - ANY_OF: any one of the provided approvers can approve (always "1 of N" semantics)
 * - ALL_SEQUENTIAL: all approvers must approve in the provided order
 * - ALL_PARALLEL: all approvers must approve, order does not matter
 *
 * Note: 'AUTO' is NOT allowed for REQUEST_APPROVAL actions. For automatic approval
 * without human intervention, use the AUTO_APPROVE action type instead.
 *
 * ANY_OF Semantics:
 * The ANY_OF pattern always means "1 of N" - a single approval from any assigned
 * approver completes the step. For more complex approval thresholds (e.g., 2 of 3),
 * use multiple sequential steps or coordinate with the Approval Action Backend APIs.
 *
 * Approvers Configuration:
 * The `approvers` array supports multiple approver configurations within a single step.
 * Each approver entry has its own type and configuration:
 * - SPECIFIC_USERS: explicit list of user IDs in `userIds`
 * - ROLE: resolves all users in `roleId`
 * - COST_CENTER_OWNER: resolves to the bill's cost center owner
 * - DEPARTMENT_HEAD: resolves to the bill's department head
 * - BILL_REQUESTER_MANAGER: resolves to the manager of the bill requester
 * - USER_APPROVAL_AUTHORITY: resolves to users with sufficient approval authority
 *
 * Timeout behavior:
 * - `timeoutHours` defines when to trigger `timeoutAction`
 * - `timeoutAction` can remind, escalate, or auto-approve
 * - `escalateToUserIds` lists users to notify/escalate on timeout
 *
 * Segregation of duties:
 * - `excludeBillRequester` prevents the bill requester from approving their own request
 */
export const RequestApprovalActionConfigSchema = z
  .object({
    /**
     * Name of the approval step displayed in the workflow canvas and history.
     */
    stepName: z.string().max(200),
    /**
     * Approval pattern that defines how many approvals are required and in what order.
     * Note: 'AUTO' is not allowed here - use AUTO_APPROVE action type instead.
     */
    approvalPattern: z.enum(['SINGLE', 'ANY_OF', 'ALL_SEQUENTIAL', 'ALL_PARALLEL']),
    /**
     * Array of approver configurations. Each entry defines a type and its required fields.
     * Multiple approvers of different types can be combined in a single step.
     */
    approvers: z.array(ApproverConfigSchema).min(1, 'At least one approver required'),
    /**
     * Timeout in hours before triggering a reminder, escalation, or auto-approval.
     */
    timeoutHours: z.number().positive().optional(),
    /**
     * Action to execute when timeoutHours elapses.
     */
    timeoutAction: z.enum(['REMIND', 'ESCALATE', 'AUTO_APPROVE']).optional(),
    /**
     * Users to notify or escalate to when a timeout occurs (used when timeoutAction is ESCALATE).
     */
    escalateToUserIds: z.array(z.string().uuid()).optional(),
    /**
     * When true, excludes the bill requester from being an approver to enforce segregation of duties.
     */
    excludeBillRequester: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (value.timeoutAction && !value.timeoutHours) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timeoutHours'],
        message: 'timeoutHours is required when timeoutAction is defined',
      });
    }

    if (
      value.timeoutAction === 'ESCALATE' &&
      (!value.escalateToUserIds || value.escalateToUserIds.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['escalateToUserIds'],
        message:
          'escalateToUserIds is required and must be non-empty when timeoutAction is ESCALATE',
      });
    }

    if (value.timeoutHours && !value.timeoutAction) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timeoutAction'],
        message: 'timeoutAction is required when timeoutHours is defined',
      });
    }
  });

/**
 * Schema for notification action config.
 *
 * The action fires a Catalog Type (see ALC-1705 and
 * `apps/api/src/modules/notifications/CONTEXT.md`) instead of free text —
 * there is no template field. Recipient resolution mirrors approval steps:
 * - SPECIFIC_USERS: explicit users in `recipientUserIds`
 * - ROLE: resolves all users in `recipientRoleId`
 * - COST_CENTER_OWNER: resolves to the bill's cost center owner(s)
 * - DEPARTMENT_HEAD: resolves to the bill's department head(s)
 *
 * Note: BILL_REQUESTER_MANAGER is intentionally excluded, same reason as
 * `ApproverConfigSchema` above — there is no data path from a bill requester
 * (`User`) to "their manager" (no `User.managerId`, no `User`↔`HcmEmployee`
 * link). It can be added back once that link exists.
 */
export const SendNotificationActionConfigSchema = z
  .object({
    recipientType: z.enum(['SPECIFIC_USERS', 'ROLE', 'COST_CENTER_OWNER', 'DEPARTMENT_HEAD']),
    recipientUserIds: z.array(z.string().uuid()).optional(),
    recipientRoleId: z.string().uuid().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.recipientType === 'SPECIFIC_USERS' &&
      (!value.recipientUserIds || value.recipientUserIds.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recipientUserIds'],
        message: 'recipientUserIds is required when recipientType is SPECIFIC_USERS',
      });
    }

    if (value.recipientType === 'ROLE' && !value.recipientRoleId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recipientRoleId'],
        message: 'recipientRoleId is required when recipientType is ROLE',
      });
    }
  });

/**
 * Schema for automatic approval action config.
 *
 * Used for steps that do not require human intervention, typically as:
 * - Terminal approval when conditions are met (e.g., low-value invoices)
 * - Timeout fallback when escalation should auto-approve
 * - Default action for specific triggers
 */
export const AutoApproveActionConfigSchema = z.object({
  /**
   * Optional reason captured for audit/history when auto-approving.
   */
  reason: z.string().optional(),
});

export const RequirePurchaseOrderActionConfigSchema = z.object({}).strict();

export const ContractReviewEnqueueIntakeActionConfigSchema = z.object({}).strict();

// Export inferred TypeScript types from schemas
export type ActionConfigItem = z.infer<typeof ActionConfigItemSchema>;
export type SegmentValueConfigItem = z.infer<typeof SegmentValueConfigItemSchema>;
export type SetCostCenterActionConfig = z.infer<typeof SetCostCenterActionConfigSchema>;
export type SetCustomSegmentActionConfig = z.infer<typeof SetCustomSegmentActionConfigSchema>;
export type SetDepartmentActionConfig = z.infer<typeof SetDepartmentActionConfigSchema>;
export type SetGlAccountActionConfig = z.infer<typeof SetGlAccountActionConfigSchema>;
export type SetManagerialAccountActionConfig = z.infer<
  typeof SetManagerialAccountActionConfigSchema
>;
export type SetOriginBankAccountActionConfig = z.infer<
  typeof SetOriginBankAccountActionConfigSchema
>;
export type StandardizedActionConfig = z.infer<typeof StandardizedActionConfigSchema>;
export type ApproverConfig = z.infer<typeof ApproverConfigSchema>;
export type RequestApprovalActionConfig = z.infer<typeof RequestApprovalActionConfigSchema>;
export type SendNotificationActionConfig = z.infer<typeof SendNotificationActionConfigSchema>;
export type AutoApproveActionConfig = z.infer<typeof AutoApproveActionConfigSchema>;
export type RequirePurchaseOrderActionConfig = z.infer<
  typeof RequirePurchaseOrderActionConfigSchema
>;
export type ContractReviewEnqueueIntakeActionConfig = z.infer<
  typeof ContractReviewEnqueueIntakeActionConfigSchema
>;

/**
 * Legacy approval action config shape (pre-multi-approver model).
 * Used for backward compatibility when loading older workflow configs.
 */
interface LegacyRequestApprovalActionConfig {
  stepName: string;
  approvalPattern: 'SINGLE' | 'ANY_OF' | 'ALL_SEQUENTIAL' | 'ALL_PARALLEL';
  approverType:
    | 'SPECIFIC_USERS'
    | 'ROLE'
    | 'COST_CENTER_OWNER'
    | 'DEPARTMENT_HEAD'
    | 'BILL_REQUESTER_MANAGER'
    | 'USER_APPROVAL_AUTHORITY';
  approverUserIds?: string[];
  approverRoleId?: string;
  requiredApprovalCount?: number;
  timeoutHours?: number;
  timeoutAction?: 'REMIND' | 'ESCALATE' | 'AUTO_APPROVE';
  escalateToUserIds?: string[];
  excludeBillRequester?: boolean;
}

/**
 * Migrates a legacy approval action config to the new multi-approver format.
 * If the config already has an `approvers` array, it is returned unchanged.
 *
 * Note: BILL_REQUESTER_MANAGER is migrated to DEPARTMENT_HEAD as a fallback
 * since BILL_REQUESTER_MANAGER is not yet implemented in the backend.
 *
 * @param config - A config object that may be in legacy or new format
 * @returns A config object in the new format with `approvers` array
 */
export function migrateLegacyApprovalConfig(
  config: Record<string, unknown>,
): RequestApprovalActionConfig {
  // If already migrated (has 'approvers' array), validate and return
  if ('approvers' in config && Array.isArray(config.approvers)) {
    const result = RequestApprovalActionConfigSchema.safeParse(config);
    if (result.success) {
      return result.data;
    }
    // If validation fails, try to fix common issues
    console.warn('Approval config has approvers but failed validation, attempting fix', {
      issues: result.error.issues,
    });
  }

  // Convert legacy single-approver format to new array format
  const legacyConfig = config as unknown as LegacyRequestApprovalActionConfig;
  const approverType = legacyConfig.approverType ?? 'SPECIFIC_USERS';

  let approver: ApproverConfig;
  switch (approverType) {
    case 'SPECIFIC_USERS':
      approver = {
        type: 'SPECIFIC_USERS',
        userIds: legacyConfig.approverUserIds ?? [],
      };
      break;
    case 'ROLE':
      approver = {
        type: 'ROLE',
        roleId: legacyConfig.approverRoleId ?? '',
      };
      break;
    case 'COST_CENTER_OWNER':
      approver = { type: 'COST_CENTER_OWNER' };
      break;
    case 'DEPARTMENT_HEAD':
      approver = { type: 'DEPARTMENT_HEAD' };
      break;
    case 'BILL_REQUESTER_MANAGER':
      // BILL_REQUESTER_MANAGER is not implemented - fallback to DEPARTMENT_HEAD
      console.warn(
        'BILL_REQUESTER_MANAGER approver type is not implemented, falling back to DEPARTMENT_HEAD',
      );
      approver = { type: 'DEPARTMENT_HEAD' };
      break;
    case 'USER_APPROVAL_AUTHORITY':
      approver = { type: 'USER_APPROVAL_AUTHORITY' };
      break;
    default:
      approver = { type: 'SPECIFIC_USERS', userIds: [] };
  }

  return {
    stepName: legacyConfig.stepName ?? '',
    approvalPattern: legacyConfig.approvalPattern ?? 'SINGLE',
    approvers: [approver],
    timeoutHours: legacyConfig.timeoutHours,
    timeoutAction: legacyConfig.timeoutAction,
    escalateToUserIds: legacyConfig.escalateToUserIds,
    excludeBillRequester: legacyConfig.excludeBillRequester ?? false,
  };
}
